// Reads the paper "stock register" workbook (one sheet per item, columns:
// DATE · VENDOR NAME · BILL NO / V. NO · OPEN QTY · RECEIPTS QTY · CONTRACTOR NAME ·
// ISSUED QTY · BALANCE QTY · TRUCK NO · LOCATION · REMARKS) into plain entries.
//
// - A sheet can hold several tables (the title + header repeat per register page);
//   every header row starts a new block.
// - A row with both Receipts and Issued is a direct delivery: it becomes a receipt
//   followed by an issue.
// - Rows without a date or without a quantity (totals, pre-filled formulas) are skipped.
// - Sheets with an ITEM NAME / PARTICULAR column carry the item on every row.

const norm = (s) =>
  String(s ?? "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

export const cellValue = (v) => {
  if (v === null || v === undefined) return null;
  if (typeof v === "object" && !(v instanceof Date)) {
    if (Array.isArray(v.richText)) return v.richText.map((t) => t.text).join("");
    if (v.result !== undefined) return cellValue(v.result);
    if (v.text !== undefined) return cellValue(v.text);
    return null;
  }
  return v;
};

const text = (v) => {
  const x = cellValue(v);
  if (x === null) return "";
  if (x instanceof Date) return x.toISOString().slice(0, 10);
  return String(x).trim().replace(/\s+/g, " ");
};

const toNumber = (v) => {
  const x = cellValue(v);
  if (typeof x === "number" && Number.isFinite(x)) return x;
  if (typeof x === "string" && /^-?\d+(\.\d+)?$/.test(x.trim())) return Number(x);
  return null;
};

const pad = (n) => String(n).padStart(2, "0");
const toDateKey = (v) => {
  const x = cellValue(v);
  if (x instanceof Date && !Number.isNaN(x.getTime())) return x.toISOString().slice(0, 10);
  if (typeof x === "string") {
    const m = x.trim().match(/^(\d{1,2})[./-](\d{1,2})[./-](\d{2,4})$/);
    if (m) {
      const y = m[3].length === 2 ? `20${m[3]}` : m[3];
      return `${y}-${pad(m[2])}-${pad(m[1])}`;
    }
  }
  return null;
};

// header text -> field. Returns the field name or null.
const fieldOf = (h) => {
  const n = norm(h);
  if (!n) return null;
  if (n === "date") return "date";
  if (n === "sno" || n === "srno") return "sno";
  if (n.startsWith("vendorname") || n === "party") return "party";
  if (n.startsWith("itemname") || n.startsWith("particul")) return "item";
  if (n.startsWith("billno") || n === "vno" || n.startsWith("vno")) return "bill";
  if (n.startsWith("openqty")) return "open";
  if (n.startsWith("receipt") || n.startsWith("recqty")) return "rec";
  if (n.startsWith("contractor")) return "con";
  if (n.startsWith("issue")) return "iss";
  if (n.startsWith("balance") || n.startsWith("inhand")) return "bal";
  if (n.startsWith("truck")) return "truck";
  if (n.startsWith("location")) return "loc";
  if (n.startsWith("remark")) return "rem";
  return null;
};

const isHeaderRow = (cells) => {
  const fields = cells.map(fieldOf);
  return (
    fields.includes("date") &&
    fields.filter((f) => f === "rec" || f === "iss" || f === "bal").length >= 2
  );
};

const UNIT_WORDS = {
  kgs: "kg", kg: "kg", cft: "cft", sqf: "sq ft", mtr: "mtr", mtrs: "mtr", m: "mtr",
  nos: "pcs", pcs: "pcs", pc: "pcs", ltr: "ltr", litter: "ltr", litre: "ltr", ml: "mL",
  m3: "m3", cum: "m3", box: "box", bag: "bag", bags: "bag", pairs: "pcs", feet: "feet",
};

// last unit word in a header like "RECEIPTS QTY NOS M3" -> "m3"
const unitFromText = (s) => {
  const words = String(s || "")
    .toLowerCase()
    .replace(/[^a-z0-9 ]/g, " ")
    .split(/\s+/)
    .filter(Boolean);
  for (let i = words.length - 1; i >= 0; i -= 1) {
    if (UNIT_WORDS[words[i]]) return UNIT_WORDS[words[i]];
  }
  return "";
};

const titleToItem = (title, sheetName) => {
  let t = String(title || "").trim();
  if (!t) return String(sheetName || "").trim();
  t = t.split(/\bSTOCK\b|\(RECEIPTS|\bRECEIPTS\b/i)[0];
  t = t.replace(/\bPAGE\b.*$/i, "").replace(/\(\s*\)/g, "");
  t = t.replace(/\b(KGS?|CFT|MTR|PCS|NOS|LTR|ML)\s*$/i, "");
  t = t.replace(/[\s\-–,(]+$/g, "").replace(/\s+/g, " ").trim();
  return t || String(sheetName || "").trim();
};

const round3 = (n) => Math.round(n * 1000) / 1000;

/**
 * @param worksheets ExcelJS worksheets
 * @param todayKey   "YYYY-MM-DD" — rows after this are skipped
 * @returns [{ name, itemName, unit, perRowItem, status, rows, counts, ... }]
 */
export const parseRegisterWorkbook = (worksheets, todayKey) =>
  worksheets.map((ws) => {
    const out = {
      name: ws.name,
      itemName: "",
      unit: "",
      perRowItem: false,
      status: "ok",
      reason: "",
      rows: [],
      receipts: 0,
      issues: 0,
      receiptQty: 0,
      issueQty: 0,
      opening: 0,
      computedClosing: 0,
      sheetClosing: null,
      skipped: [],
    };

    let cols = null;
    let blocks = 0;
    let title = "";
    let headerText = "";
    let lastDate = null;
    const pendingDate = []; // numbered rows before the first dated row
    let lastBal = null; // last numeric balance seen in the current block
    let blockHasData = false;
    let sheetClosingSum = 0;
    let anyBalance = false;

    const closeBlock = () => {
      if (blockHasData && lastBal !== null) {
        sheetClosingSum += lastBal;
        anyBalance = true;
      }
      lastBal = null;
      blockHasData = false;
    };

    ws.eachRow({ includeEmpty: false }, (row, rowNumber) => {
      const cells = [];
      for (let c = 1; c <= Math.min(row.cellCount || 0, 30); c += 1) cells.push(cellValue(row.getCell(c).value));

      if (isHeaderRow(cells)) {
        closeBlock();
        cols = {};
        cells.forEach((v, i) => {
          const f = fieldOf(v);
          if (f && cols[f] === undefined) cols[f] = i;
        });
        blocks += 1;
        if (blocks === 1) {
          headerText = `${text(cells[cols.rec])} ${text(cells[cols.iss])}`;
          out.perRowItem = cols.item !== undefined;
        }
        return;
      }
      if (!cols) {
        // text above the first header is the sheet title
        if (!title) {
          const t = cells.map(text).find((x) => x.length > 3);
          if (t) title = t;
        }
        return;
      }

      const get = (f) => (cols[f] !== undefined ? cells[cols[f]] : null);
      const rec = toNumber(get("rec"));
      const iss = toNumber(get("iss"));
      const bal = toNumber(get("bal"));
      const open = toNumber(get("open"));
      const hasQty = (rec && rec > 0) || (iss && iss > 0);
      if (bal !== null && hasQty) {
        lastBal = bal;
      }
      if (!hasQty) return;
      blockHasData = true;

      let dateKey = toDateKey(get("date"));
      let dateFilled = false;
      if (!dateKey) {
        // a numbered entry (or a continuation line of a multi-item bill) with the
        // date left blank takes the previous row's date; an un-numbered row with
        // no date is a total / note line
        const numbered = toNumber(get("sno")) !== null || (cols.item !== undefined && text(get("item")));
        if (!numbered) {
          out.skipped.push({ row: rowNumber, why: "no date (total / note row)" });
          return;
        }
        dateKey = lastDate;
        dateFilled = true;
      } else {
        lastDate = dateKey;
      }
      if (dateKey && todayKey && dateKey > todayKey) {
        out.skipped.push({ row: rowNumber, why: `date ${dateKey} is in the future` });
        return;
      }

      if (out.rows.length === 0 && open && open > 0 && blocks === 1) out.opening = open;

      const base = {
        row: rowNumber,
        sheet: ws.name,
        date: dateKey || "",
        billNo: text(get("bill")),
        truckNo: text(get("truck")),
        location: text(get("loc")),
        remarks: text(get("rem")),
        itemName: out.perRowItem ? text(get("item")) : "",
        dateFilled,
      };
      if (out.perRowItem && !base.itemName) {
        out.skipped.push({ row: rowNumber, why: "no item name" });
        return;
      }
      if (!dateKey) {
        pendingDate.push(rowNumber);
      }
      if (rec && rec > 0) {
        out.rows.push({ ...base, seq: out.rows.length, entryType: "add", qty: round3(rec), party: text(get("party")) });
        out.receipts += 1;
        out.receiptQty += rec;
      }
      if (iss && iss > 0) {
        out.rows.push({ ...base, seq: out.rows.length, entryType: "issued", qty: round3(iss), party: text(get("con")) });
        out.issues += 1;
        out.issueQty += iss;
      }
    });
    closeBlock();

    // rows that came before any dated row take the first date that follows
    if (pendingDate.length) {
      const firstDated = out.rows.find((r) => r.date);
      out.rows.forEach((r) => {
        if (!r.date) {
          if (firstDated) r.date = firstDated.date;
        }
      });
      out.rows = out.rows.filter((r) => {
        if (r.date) return true;
        out.skipped.push({ row: r.row, why: "no date anywhere on the sheet" });
        return false;
      });
    }
    out.datesFilled = out.rows.filter((r) => r.dateFilled).length;

    if (!cols) {
      out.status = "skipped";
      out.reason = "Not a stock register sheet (no DATE / RECEIPTS / ISSUED header).";
      return out;
    }
    if (!out.rows.length) {
      out.status = "skipped";
      out.reason = "No dated entries.";
      return out;
    }

    out.itemName = out.perRowItem ? "(item on every row)" : titleToItem(title, ws.name);
    out.unit = unitFromText(headerText) || unitFromText(title) || "pcs";
    // "RECEIPTS QTY NOS M3" is copy-pasted onto fittings; only RMC / concrete is really m3
    if (out.unit === "m3" && !/rmc|concrete/i.test(out.itemName)) out.unit = "pcs";
    if (/\bTMT\b|BINDING WIRE/i.test(out.itemName) && out.unit === "pcs") out.unit = "kg";
    out.receiptQty = round3(out.receiptQty);
    out.issueQty = round3(out.issueQty);
    out.computedClosing = round3(out.opening + out.receiptQty - out.issueQty);
    out.sheetClosing = anyBalance ? round3(sheetClosingSum) : null;
    // an opening balance that has no receipt behind it becomes its own receipt
    if (out.opening > 0 && !out.perRowItem) {
      const first = out.rows[0];
      out.rows.unshift({
        row: 0, // own import key — never clashes with the first real row
        seq: -1,
        sheet: ws.name,
        date: first.date,
        billNo: "",
        truckNo: "",
        location: "",
        remarks: "Opening balance",
        itemName: "",
        entryType: "add",
        qty: round3(out.opening),
        party: "",
      });
    }
    return out;
  });

// ── name matching (mirrors partyKey in the backend) ──────────────────────────
// "K B K", "K.B.K", "kbk" → "kbk"
export const partyKey = (s) => String(s ?? "").toLowerCase().replace(/[^a-z0-9]/g, "");

const GENERIC = new Set([
  "store", "stores", "traders", "trader", "trading", "enterprises", "enterprise", "co", "company",
  "agency", "agencies", "suppliers", "supplier", "ltd", "limited", "pvt", "private", "and", "sons", "brothers", "bros",
]);
// "CHEEMA STORE" → "cheema": same base, different spelling → suggested merge
export const partyBase = (s) => {
  const t = String(s ?? "").toLowerCase().split(/[^a-z0-9]+/).filter(Boolean);
  while (t.length > 1 && GENERIC.has(t[t.length - 1])) t.pop();
  return t.join("");
};
